/**
 * Token + session storage.
 *
 * ---------------------------------------------------------------------------
 * SECURITY NOTE — localStorage is weaker than the Flutter app's storage.
 * ---------------------------------------------------------------------------
 * The Flutter client keeps these values in `flutter_secure_storage`, which is
 * backed by the iOS Keychain / Android Keystore. There is no browser equivalent:
 * localStorage is readable by any JavaScript running on the origin, so a single
 * XSS bug exfiltrates a valid access AND refresh token.
 *
 * The genuinely more secure option is httpOnly + Secure + SameSite cookies,
 * which JavaScript cannot read at all. That is deliberately OUT OF SCOPE here:
 * the backend's contract is `Authorization: Bearer <access>` (pinned in
 * refactor/docs/PARITY_NOTES.md under "JWT claim names diverge from SimpleJWT"
 * as one of the things held identical because the Flutter client depends on it),
 * and moving to cookies means a backend change — Set-Cookie on login, a CSRF
 * strategy for every mutating request, and a cookie-aware refresh endpoint —
 * applied while the Flutter app is still live on the same endpoints.
 *
 * So: localStorage, the standard pattern for a Bearer-token SPA, with the
 * exposure written down rather than assumed away. If this app ever holds
 * something worth stealing beyond a session, revisit it as a deliberate
 * both-backends change, not a frontend-only tweak.
 *
 * Storage keys match the Flutter client's (constants.dart:63-65) so a browser
 * that has run the Flutter web build reuses the same slots rather than
 * accumulating a second, divergent session.
 */
import type { User } from '../types/user';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_DATA_KEY = 'user_data';

/**
 * localStorage throws in Safari private mode and when storage is disabled by
 * policy. A read failure must degrade to "logged out", never crash the app.
 */
function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the session simply will not survive a reload */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

export function getAccessToken(): string | null {
  return safeRead(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return safeRead(REFRESH_TOKEN_KEY);
}

export function setAccessToken(token: string): void {
  safeWrite(ACCESS_TOKEN_KEY, token);
}

/**
 * Persists BOTH tokens.
 *
 * Storing the refresh token on every refresh is REQUIRED, not optional, and is
 * the one place this client deliberately does not copy the Flutter one. The
 * backend sets ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION, so
 * `POST auth/refresh/` blacklists the presented refresh token and returns a NEW
 * pair (refactor/backend/src/modules/auth/routes.ts:243-254). The Flutter client
 * saves only `response.data['access']` (api_client.dart:90-94) and keeps the old
 * refresh token, so its SECOND refresh presents a blacklisted token and fails —
 * see the report accompanying this scaffold.
 */
export function setTokens(tokens: { access: string; refresh: string }): void {
  safeWrite(ACCESS_TOKEN_KEY, tokens.access);
  safeWrite(REFRESH_TOKEN_KEY, tokens.refresh);
}

export function getStoredUser(): User | null {
  const raw = safeRead(USER_DATA_KEY);
  if (raw === null) return null;
  try {
    // Trusted only as a cache of a previous `/auth/me/`-shaped payload. It is
    // re-validated by the server on the first authenticated request; a 401 there
    // clears it via the api client's unauthorized handler.
    return JSON.parse(raw) as User;
  } catch {
    safeRemove(USER_DATA_KEY);
    return null;
  }
}

export function setStoredUser(user: User): void {
  safeWrite(USER_DATA_KEY, JSON.stringify(user));
}

/** Port of Flutter's `StorageService.clear()` — drops the whole session. */
export function clearSession(): void {
  safeRemove(ACCESS_TOKEN_KEY);
  safeRemove(REFRESH_TOKEN_KEY);
  safeRemove(USER_DATA_KEY);
}
